/**
 * The Guardrails list. One row per guardrail capability, with a pill showing the
 * direction it runs in. Clicking a row opens that capability.
 */
import { ChevronRight } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { GuardrailTree } from "../types";
import type { GuardrailClause } from "../guardrailClauses";

export interface GuardrailRow {
  guardrail: GuardrailTree;
  clause?: GuardrailClause;
}

interface GuardrailsListProps {
  rows: GuardrailRow[];
  onOpen: (row: GuardrailRow) => void;
  isLoading: boolean;
  filter: string;
}

export const GuardrailsList = ({
  rows,
  onOpen,
  isLoading,
  filter,
}: GuardrailsListProps) => (
  <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-brand-border bg-brand-card">
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {isLoading ? (
        <p className="py-8 text-center text-sm text-brand-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-brand-muted">
          No guardrails found{filter ? ` matching “${filter}”` : ""}.
        </p>
      ) : (
        rows.map((row) => (
          <button
            type="button"
            key={row.guardrail.id}
            onClick={() => onOpen(row)}
            className={cn(
              "group flex flex-none cursor-pointer items-center gap-3.5 border-b border-gray-100 px-4 py-3.5 text-left transition-colors hover:bg-brand-primary/5",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-unlikely/40"
            )}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold">
                  {row.clause?.title ?? row.guardrail.title}
                </span>
                {row.clause && (
                  <span className="flex-none rounded-full bg-brand-primary/[0.08] px-1.5 py-px text-[11px] text-brand-muted">
                    {row.guardrail.title}
                  </span>
                )}
              </span>
              {row.clause?.explanation && (
                <span className="truncate text-xs text-brand-muted">
                  {row.clause.explanation}
                </span>
              )}
            </span>

            <span className="flex-none rounded-full border border-brand-border bg-brand-background px-2 py-px text-[11px] font-medium text-brand-muted">
              {row.guardrail.guardrailType === "INPUT" ? "Input" : "Output"}
            </span>

            <ChevronRight
              className="h-4 w-4 flex-none text-gray-300 transition-colors group-hover:text-unlikely"
              aria-hidden={true}
            />
          </button>
        ))
      )}
    </div>
  </div>
);
